import React from 'react';
import ProjectLayout from '../ProjectLayout';
import { Sidenote } from '../../Sidenote';
import { Cite, Citet } from '../../Citations';
import { InlineMath, BlockMath } from '../../Latex';
import './ProjectPage.css'; // Custom styles for this project

// Paste BibTeX entries straight in — keys become the <Cite id="..."> keys.
const references = `
@article{s1_2025,
  author  = {Muennighoff, Niklas and Yang, Zitong and Shi, Weijia and others},
  title   = {s1: Simple test-time scaling},
  journal = {arXiv preprint arXiv:2501.19393},
  year    = {2025},
  url     = {https://arxiv.org/abs/2501.19393},
}

@misc{novasky2025,
  author = {{NovaSky Team}},
  title  = {Reducing Overthinking in Reasoning Models},
  year   = {2025},
  url    = {https://novasky-ai.github.io/posts/reduce-overthinking/},
  short  = {NovaSky},
}
@misc{shandilya2024tacorltaskawareprompt,
    title={TACO-RL: Task Aware Prompt Compression Optimization with Reinforcement Learning}, 
    author={Shivam Shandilya and Menglin Xia and Supriyo Ghosh and Huiqiang Jiang and Jue Zhang and Qianhui Wu and Victor Rühle},
    year={2024},
    eprint={2409.13035},
    archivePrefix={arXiv},
    primaryClass={cs.CL},
    url={https://arxiv.org/abs/2409.13035}, 
}
    @misc{jiang2023llmlinguacompressingpromptsaccelerated,
      title={LLMLingua: Compressing Prompts for Accelerated Inference of Large Language Models}, 
      author={Huiqiang Jiang and Qianhui Wu and Chin-Yew Lin and Yuqing Yang and Lili Qiu},
      year={2023},
      eprint={2310.05736},
      archivePrefix={arXiv},
      primaryClass={cs.CL},
      url={https://arxiv.org/abs/2310.05736},
}

@misc{neubig2025reasoning,
  author       = {Neubig, Graham},
  title        = {CMU LLM Inference (9): Reasoning Models},
  howpublished = {Lecture, CMU CS 11-765},
  year         = {2025},
  url          = {https://www.youtube.com/watch?v=6-mSbIPI4tc},
  note         = {YouTube video.},
}

@misc{chuang2024learningcompresspromptnatural,
      title={Learning to Compress Prompt in Natural Language Formats}, 
      author={Yu-Neng Chuang and Tianwei Xing and Chia-Yuan Chang and Zirui Liu and Xun Chen and Xia Hu},
      year={2024},
      eprint={2402.18700},
      archivePrefix={arXiv},
      primaryClass={cs.CL},
      url={https://arxiv.org/abs/2402.18700}, 
}

@misc{shao2024deepseekmath,
  title={DeepSeekMath: Pushing the Limits of Mathematical Reasoning in Open Language Models},
  author={Zhihong Shao and Peiyi Wang and Qihao Zhu and Runxin Xu and Junxiao Song and Xiao Bi and Haowei Zhang and Mingchuan Zhang and Y. K. Li and Y. Wu and Daya Guo},
  year={2024},
  eprint={2402.03300},
  archivePrefix={arXiv},
  primaryClass={cs.CL},
  url={https://arxiv.org/abs/2402.03300},
}

@misc{schulman2017ppo,
  title={Proximal Policy Optimization Algorithms},
  author={John Schulman and Filip Wolski and Prafulla Dhariwal and Alec Radford and Oleg Klimov},
  year={2017},
  eprint={1707.06347},
  archivePrefix={arXiv},
  url={https://arxiv.org/abs/1707.06347},
}

@inproceedings{khashabi2018multirc,
  author    = {Khashabi, Daniel and Chaturvedi, Snigdha and Roth, Michael and Upadhyay, Shyam and Roth, Dan},
  title     = {Looking Beyond the Surface: A Challenge Set for Reading Comprehension over Multiple Sentences},
  booktitle = {NAACL-HLT},
  year      = {2018},
  url       = {https://aclanthology.org/N18-1023/},
}
`;

const CoT = ({ project }) => {
  return (
    <ProjectLayout project={project} className="cot-page" references={references}>
      <h2>Introduction</h2>
      
      <p>
      I first came up with this project when DeepSeek-V1 <Cite id = "shao2024deepseekmath"/> initially released in early 2025.<Sidenote>It has now been about a year since I "completed" this original project, and it is honestly not the most exceptional. What I will say though is that it was a great experience to onramp myself onto post-training, reinforcement learning, and all of the hurdles that exist within those spaces. This project inspired me to work in AI research; I will always appreciate this project. </Sidenote>
      Around this time, Chain-Of-Thought reasoning was super novel; I believe it coincided with the release of the s1 paper from <Citet id="s1_2025" />, Sky-T1, and gpt-o1. Many blog posts were also being released analyzing these long "reasoning" traces, finding that they were ogoing on tangents<Cite id="novasky2025" />, were often not very faithful, and exploded inference costs. I saw this as an opportunity to explore what a model was thinking, somewhat ignoring this lack of faithfulness. 
      The original focus of this project was to hopefully learn a network: <InlineMath
        math="
          f(X) = Y
        "
      />, where <InlineMath
        math="
          X
        "
      /> is the original prompt and <InlineMath
        math="
          Y
        "
      /> is a prompt that leads to a shorter chain of thought with equivalent performance.<Sidenote>Performance here implying evaluation scores on verifiable tasks. The method in which we are weighing rewards becomes very messy, which we discuss further down.</Sidenote> This is what led to the name, "Compression of Thought". However, if you were to look through the entirety of this project, that is not at all what this focuses on. In fact, this project mainly focuses on simply compressing prompts. Looking back, I don't completely remember why the focus changed so much, but I attribute it post-hoc to the following reasons (in order of precedence):
      <ul>
              <li>There was much more literature that existed on prompt compression <Cite id="shandilya2024tacorltaskawareprompt, jiang2023llmlinguacompressingpromptsaccelerated, chuang2024learningcompresspromptnatural"></Cite></li>
              <li>I could barely fit outputs of a 3B model on the compute cluster I was using, so reasoning traces wouldn't fit</li>
              <li>I didn't understand how reasoning models functioned as well as I needed to <Sidenote>I personally think Graham Neubig's lectures for his CMU CS 11-765 course are a really great resource here (and generally for Language Models), particularly this lecture <Cite id="neubig2025reasoning" /></Sidenote></li>

          </ul>
      This projected then just shifted into its final form, a model that learns to compress prompts through purely a reward signal. 

      </p>

      <h2>Methods</h2>

      <h3>Setup</h3>
      <p>
          The training loop closely follows Nano-Capsulator<Cite id="chuang2024learningcompresspromptnatural" />, with a few changes that I will get into. I trained on MultiRC<Cite id="khashabi2018multirc" />, a reading comprehension dataset where every sample is a passage, a question about that passage, and a proposed answer labeled as correct or incorrect. The full dataset has 27,243 samples; I used a random subset of 1,520 of them.<Sidenote>Memory constraints, I didn't really understand quantization, and I was spending so much time on fixing errors that I couldn't wait for all samples to complete.</Sidenote> There are two models in the loop: a trainable model that receives the passage and generates a compressed version of it, and a frozen model that is asked, given only the compressed passage, whether the proposed answer to the question is correct. That "yes" or "no", along with how much shorter the compression is, becomes the reward. I used OLMo-2-1B and Llama-3.2-1B for both roles, in all four pairings.
      </p>

      <figure className="cot-diagram" aria-label="Diagram of the Compression of Thought training loop">
        <div className="cd-box">
          <span className="cd-box-label">Passage</span>
          <span className="cd-box-sub">a passage from MultiRC, with a question and a proposed answer</span>
        </div>

        <div className="cd-down" aria-hidden="true">
          <span className="cd-down-label">fed to the compressor — no instruction, no template</span>
        </div>

        <div className="cd-box cd-box-trainable">
          <span className="cd-box-label">Trainable model</span>
          <span className="cd-box-sub">OLMo-2-1B or Llama-3.2-1B — the only model that learns</span>
        </div>

        <div className="cd-down" aria-hidden="true">
          <span className="cd-down-label">generates</span>
        </div>

        <div className="cd-box">
          <span className="cd-box-label">Compressed passage</span>
          <span className="cd-box-sub">whatever the model decided was worth keeping</span>
        </div>

        <div className="cd-down" aria-hidden="true">
          <span className="cd-down-label">
            combined with the question and proposed answer into an evaluation prompt
          </span>
        </div>

        <div className="cd-box cd-box-frozen">
          <span className="cd-box-label">Frozen model</span>
          <span className="cd-box-sub">
            OLMo-2-1B or Llama-3.2-1B — asked “Is this answer correct?”; no gradients
            pass through it
          </span>
        </div>

        <div className="cd-down" aria-hidden="true">
          <span className="cd-down-label">
            its “yes” / “no” is checked against the true label, and the compression
            ratio is measured
          </span>
        </div>

        <div className="cd-box cd-box-reward">
          <span className="cd-box-label">
            Reward:{' '}
            <InlineMath math="r^{(i)} = 0.6 \, r_{\text{acc}}^{(i)} + 0.4 \, r_{\text{comp}}^{(i)}" />
          </span>
          <span className="cd-box-sub">the only learning signal in the entire setup</span>
        </div>

        <div className="cd-loop" aria-hidden="true">
          <span className="cd-loop-label">GRPO update</span>
        </div>

        <figcaption>
          One training step. The reward flows back along the dashed path to update
          the trainable model only. Two forward passes total — the original passage
          is never shown to the frozen model, so nothing anchors the compression to
          the source text except the reward.
        </figcaption>
      </figure>

      <p>
          One thing worth noting: at no point do we compare the compression to the original passage. Most prior work keeps some semantic similarity term anchoring the compression to the source text, and I dropped it entirely. This was half a philosophical choice (the model should keep what <em>it</em> needs to answer correctly, and that could be independent of the original output) and half a practical one, since it cuts the training loop from three forward passes down to two.
      </p>

      <h3>The Reward Function</h3>
      <p>
          The reward is the only learning signal in the entire setup, so it deserves its own section. It has two parts. The first rewards compression, where <InlineMath math="p_i" /> is the original passage and <InlineMath math="c_i" /> is the compressed completion:
      </p>
      <BlockMath math="r_{\text{comp}}^{(i)} = 1 - \text{clip}\left( \frac{\text{len}(c_i)}{\text{len}(p_i)},\ 0.1,\ 3.0 \right)" />
      <p>
          The second rewards accuracy, where <InlineMath math="l_i" /> is the true label and <InlineMath math="\hat{l}_i" /> is what the frozen model predicted:
      </p>
      <BlockMath math="r_{\text{acc}}^{(i)} = \begin{cases} 1.0 & \hat{l}_i = l_i \\ -0.2 & \hat{l}_i \in \{\text{yes}, \text{no}\},\ \hat{l}_i \neq l_i \\ -0.5 & \hat{l}_i = \text{invalid} \end{cases}" />
      <p>
          And the final reward is just a weighted combination of the two:
      </p>
      <BlockMath math="r^{(i)} = 0.6 \cdot r_{\text{acc}}^{(i)} + 0.4 \cdot r_{\text{comp}}^{(i)}" />
      <p>
          Accuracy is weighted slightly higher to encourage compressions that still make sense.<Sidenote>I intentionally chose the weight for the invalid output to be larger than the incorrect output. The base model generally is able to product a valid output always, so if a prompt changes so much such that a model can't even answer, we screwed up a lot.</Sidenote> Choice of weights here is completely arbitrary, pretty bad science. If I came back to this project, I think a full grid sweep here is necessary.
      </p>

      <h3>Training</h3>
      <p>
          Training is done with GRPO <Cite id="shao2024deepseekmath" />. I originally wanted to try an entire grid of REINFORCE, PPO, and GRPO, but two things mainly occurred. First, having a critic model in memory would generally explode memory usage, and I wasn't experienced much with model sharding so it was a difficult process to parse through. Second, when I did get things working, the multi-objective reward promptly exploded my gradients.<Sidenote>An incredible amount of this project was spent fixing code rather than answering the research question. It taught me a lot.</Sidenote> GRPO ranks completions relative to each other within a group rather than optimizing absolute reward values, which handled the accuracy-versus-compression trade-off much more gracefully.
      </p>

      <h2>Results</h2>

      <h3>Quantitative</h3>
      <p>
          Configurations with LLaMA as the trainable model beat their OLMo counterparts essentially across the board. On average, LLaMA compressions were 88.15 tokens shorter and scored 0.05898 higher in evaluation reward. LLaMA also converged in around 100 training steps with rapid initial improvement, while OLMo took around 350. Even on pure efficiency, OLMo configurations ran at least 1.32x slower per step despite LLaMA having slightly fewer parameters. I read this as LLaMA's inductive biases simply aligning better with this kind of task.
      </p>

      <h3>Qualitative</h3>
      <p>
          This is the part of the project I find genuinely interesting. The two architectures received the exact same reward signal and the exact same dataset, and they learned completely different strategies. LLaMA compressed like a good note-taker, producing structured lists of facts. One 259-word passage became:
      </p>
      <blockquote>
          "1. Antonio is a Sicilian. 2. Marta is a Sicilian. 3. Antonio and his wife have a son. 4. Antonio has a gun. 5. Antonio is a Mafia member. 6. Antonio is an undercover FBI agent."
      </blockquote>
      <p>
          OLMo, on the other hand, tried to anticipate the question. It would generate its own question-and-answer pairs instead of actually summarizing. It is hard not to read this as the model "cheating": rather than retaining the information in the passage, it gambles on what it will be asked. LLaMA's strategy feels more intuitively right to me, since it is actually learning the information rather than guessing at the question.
      </p>
      <p>
          I honestly don't completely trust these results, I think with more training these qualitative results could definitely hold more weight. 
      </p>

      <h3>Closing Thoughts</h3>
      <p>
      I really care about education. In high school, I had some amazing teachers that I know tried really hard to help <b>me</b> succeed. I came to Stanford with that in mind, that I really wanted to try to use my skills to contribute to education and somehow help students gain the opportunities I have. I did and still do firmly believe that any student with my opportunities would have succeeded and would have been able to do great things with their lives. 
      </p>
      <p>
        I initially worked on education policy research when I came to Stanford and worked at a small EdTech startup my freshman summer. Both experiences made me feel really disillusioned with what I could do to help education, and what I could do to contribute towards my overall goals coming into school. This project kind of fixed that. In a lecture once, my advisor Ludwig Schmidt said that it was theorized that backpropagation is more efficient than the way we as humans learn. In addition to this, the whole world has seen how intelligent AI has become. I think both of these facts have actually pushed me 
        very deep into ML research and deeply understanding models can make humans smarter by helping us learn better. I thought this project was cool because I thought it could somehow help us show how models are compressing/processing information within their parameters, I think there is definitely more work to be done in this sphere, and I'm excited to work towards it!


      </p>

      <p>
          Visit the links to see more!
      </p>

      <section className="links-section">
        <h2>Project Links</h2>
        <div className="project-links">
            <a href="https://github.com/lanpouthakoun/promptcompression"
            target="_blank"
            rel="noopener noreferrer"
            >
            View Source Code
            </a>
            <a href="/files/cot/Compression-Of-Thought.pdf"
                target="_blank"
                rel="noopener noreferrer"
            >
            View Paper
            </a>
        </div>
      </section>
    </ProjectLayout>
  );
};

export default CoT;
